import { useEffect, useState } from "react";
import { usePin } from "../context/PinContext";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Lock, Fingerprint, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

function formatRemaining(ms) {
    const s = Math.ceil(ms / 1000);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return m > 0 ? `${m}m ${String(r).padStart(2, "0")}s` : `${r}s`;
}

export default function PinLockScreen() {
    const { unlock, resetPin, attempts, lockoutUntil, biomEnabled, biomAvailable, unlockWithBiometric } = usePin();
    const { logout } = useAuth();
    const [pin, setPin] = useState("");
    const [reset, setReset] = useState(false);
    const [busy, setBusy] = useState(false);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!lockoutUntil) return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [lockoutUntil]);

    // Auto-prompt biometric on mount if enabled
    useEffect(() => {
        if (biomEnabled && biomAvailable) {
            unlockWithBiometric().then((ok) => {
                if (ok) toast.success("Unlocked with biometric");
            }).catch(() => {});
        }
        // eslint-disable-next-line
    }, []);

    async function tryBiometric() {
        const ok = await unlockWithBiometric();
        if (ok) toast.success("Unlocked");
        else toast.error("Biometric failed — use PIN instead");
    }

    const remaining = lockoutUntil - now;
    const isLockedOut = remaining > 0;

    async function submit(e) {
        e?.preventDefault?.();
        if (!pin || isLockedOut) return;
        setBusy(true);
        const res = await unlock(pin);
        setBusy(false);
        if (res.ok) {
            setPin("");
            toast.success("Unlocked");
        } else if (res.lockoutUntil && res.lockoutUntil > Date.now()) {
            toast.error(
                `Too many wrong attempts. Try again in ${formatRemaining(res.lockoutUntil - Date.now())}.`
            );
            setPin("");
        } else {
            const left = Math.max(0, 5 - (res.attempts || 0));
            toast.error(
                left > 0
                    ? `Wrong PIN. ${left} attempt${left === 1 ? "" : "s"} before lockout.`
                    : "Wrong PIN."
            );
            setPin("");
        }
    }

    return (
        <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
            <Card className="w-full max-w-sm p-6 rounded-2xl border-slate-100 shadow-sm">
                <div className="flex flex-col items-center text-center">
                    <div className="h-14 w-14 rounded-2xl bg-blue-900 grid place-items-center text-white">
                        <Lock className="h-6 w-6" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-slate-900 mt-3">App Locked</h2>
                    <p className="text-sm text-slate-500 mt-1">Enter your PIN to continue</p>
                </div>

                {isLockedOut && (
                    <Card
                        className="mt-4 rounded-xl bg-rose-50 border-rose-100 p-3 flex items-start gap-2"
                        data-testid="pin-lockout-card"
                    >
                        <ShieldAlert className="h-4 w-4 text-rose-700 mt-0.5 shrink-0" />
                        <div className="text-xs text-rose-900">
                            <div className="font-semibold">Too many wrong attempts.</div>
                            <div className="mt-0.5">
                                Try again in{" "}
                                <span className="font-bold num" data-testid="pin-lockout-countdown">
                                    {formatRemaining(remaining)}
                                </span>
                            </div>
                        </div>
                    </Card>
                )}

                {!reset ? (
                    <form onSubmit={submit} className="mt-5 space-y-3">
                        <Input
                            data-testid="pin-input"
                            type="password"
                            inputMode="numeric"
                            autoFocus
                            maxLength={8}
                            value={pin}
                            disabled={isLockedOut}
                            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                            className="h-14 rounded-xl text-center text-2xl tracking-[0.5em] font-display"
                            placeholder="••••"
                        />
                        <Button
                            type="submit"
                            disabled={busy || pin.length < 4 || isLockedOut}
                            data-testid="pin-unlock-btn"
                            className="w-full h-12 rounded-xl bg-blue-900 hover:bg-blue-800 tap"
                        >
                            Unlock
                        </Button>
                        {attempts > 0 && !isLockedOut && (
                            <div className="text-[11px] text-amber-700 text-center" data-testid="pin-attempts-warn">
                                {attempts} wrong attempt{attempts === 1 ? "" : "s"}
                            </div>
                        )}
                        {biomEnabled && biomAvailable && (
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full h-11 rounded-xl tap"
                                data-testid="pin-biometric-btn"
                                onClick={tryBiometric}
                            >
                                <Fingerprint className="h-4 w-4 mr-1.5 text-blue-900" />
                                Unlock with fingerprint / Face ID
                            </Button>
                        )}
                        <div className="flex justify-between text-xs">
                            <button
                                type="button"
                                onClick={() => setReset(true)}
                                data-testid="pin-forgot-btn"
                                className="text-blue-900 font-semibold"
                            >
                                Forgot PIN?
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    await logout();
                                    window.location.href = "/login";
                                }}
                                data-testid="pin-logout-btn"
                                className="text-slate-500"
                            >
                                Sign out
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="mt-5 space-y-3">
                        <Card className="rounded-xl bg-amber-50 border-amber-100 p-4 text-xs text-amber-900">
                            <b>SMS/Email OTP coming soon.</b> For now, you can reset the PIN by signing out and signing back in with your username & password — your work data is safe in the cloud.
                        </Card>
                        <Button
                            data-testid="pin-reset-confirm"
                            onClick={() => {
                                resetPin();
                                toast.success("PIN cleared. You can set a new one in Settings.");
                            }}
                            className="w-full h-12 rounded-xl bg-blue-900 hover:bg-blue-800"
                        >
                            Clear PIN now
                        </Button>
                        <Button variant="ghost" className="w-full" onClick={() => setReset(false)}>
                            Back
                        </Button>
                    </div>
                )}
            </Card>
        </div>
    );
}
